import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useFleetData, SHIPS_LIST_COLS } from "./FleetDataContext";
import { supabase } from "./supabaseClient";
import Nav from "./Nav";
import { LazyImage } from "./FleetPage";
import FleetFilterBar, { getShipStatus, SHIP_STATUS } from "./FleetFilterBar";
import { EventCard as SharedEventCard, normalizeAttendees as normalizeEventAttendees, EventFormModal, DEFAULT_RSVP_OPTIONS, EventsCalendar } from "./EventsPage";

const ACCENT = "#ce93d8";
const GUEST_BADGE_STYLE = { fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 2, color: '#ff9800', border: '1px solid rgba(255,152,0,0.4)', padding: '1px 6px', background: 'rgba(255,152,0,0.08)', marginLeft: 6, verticalAlign: 'middle' };
function GuestBadge({ member }) { return member?.member_status === 'guest' ? <span style={GUEST_BADGE_STYLE}>GUEST</span> : null; }

const NAV_LINKS = [
  { label: "Home", href: "/", external: false },
  { label: "Hierarchy", href: "/hierarchy", external: false },
  { label: "Events", href: "/events", external: false },
];


const ALL_OPERATIONS = [
  { label: "Exploration",    color: "#34d399", icon: "🌌" },
  { label: "Mining",         color: "#fcd657", icon: "⛏" },
  { label: "Transport",      color: "#81c784", icon: "📦" },
  { label: "Salvage",        color: "#a78bfa", icon: "🔩" },
  { label: "Piracy",         color: "#ef5350", icon: "☠" },
  { label: "Combat",         color: "#ff7043", icon: "💥" },
  { label: "Bounty Hunting", color: "#f87171", icon: "🎯" },
  { label: "Medical",        color: "#4fc3f7", icon: "🏥" },
  { label: "Engineering",    color: "#60a5fa", icon: "🔧" },
  { label: "Boarding",       color: "#e040fb", icon: "⚔" },
  { label: "Defense",        color: "#ce93d8", icon: "🛡" },
  { label: "Reconnaissance", color: "#34d399", icon: "👁" },
  { label: "Racing",         color: "#facc15", icon: "🏁" },
  { label: "Trading",        color: "#81c784", icon: "💰" },
  { label: "Research",       color: "#4fc3f7", icon: "🔬" },
  { label: "Escort",         color: "#fcd657", icon: "🚀" },
];

const DAYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
const DAY_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function StarField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const stars = Array.from({ length: 300 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.6 + 0.3,
      a: Math.random(), speed: Math.random() * 0.004 + 0.001,
    }));
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.a += s.speed;
        if (s.a > 1) s.a = 0;
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${Math.sin(s.a * Math.PI) * 0.8 + 0.1})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100vh", pointerEvents: "none", zIndex: 0 }} />;
}

function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(28px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`, ...style }}>
      {children}
    </div>
  );
}

function SectionLabel({ children, color = ACCENT }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
      <div style={{ width: 40, height: 2, background: color, flexShrink: 0 }} />
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 4, color, textTransform: "uppercase" }}>{children}</span>
    </div>
  );
}

function Avatar({ src, name, size = 40, color = ACCENT }) {
  const [err, setErr] = useState(false);
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${color}55` }}>
      {src && !err
        ? <img src={src} alt={name} onError={() => setErr(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : <span style={{ fontFamily: "'Orbitron'", fontSize: size * 0.38, color, fontWeight: 700 }}>{name?.charAt(0)?.toUpperCase() || "?"}</span>
      }
    </div>
  );
}

function fmtHour(h) {
  h = Math.round(((h % 24) + 24) % 24);
  if (h === 0 || h === 24) return "12 am";
  if (h === 12) return "12 pm";
  return h < 12 ? `${h} am` : `${h - 12} pm`;
}

function getOffsetHours(tz) {
  try {
    const date = new Date();
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric", hour12: false }).formatToParts(date);
    const p = {};
    parts.forEach(part => { p[part.type] = parseInt(part.value, 10); });
    const tzMs = Date.UTC(p.year, p.month - 1, p.day, p.hour === 24 ? 0 : p.hour, p.minute, p.second);
    return Math.round((tzMs - date.getTime()) / 3600000 * 4) / 4;
  } catch { return 0; }
}

// Get per-team role for a member, falling back to legacy team_role
function getTeamRole(member, teamName) {
  if (member.team_roles && member.team_roles[teamName]) return member.team_roles[teamName];
  return member.team_role || "";
}

// Get per-team sub_group for a member, falling back to legacy sub_group
function getTeamSubGroup(member, teamName) {
  if (member.sub_groups && member.sub_groups[teamName]) return member.sub_groups[teamName];
  return member.sub_group || "";
}

// Normalize array fields
function toArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  return [val];
}

const ROLE_PRIORITY = {
  "Captain": 0, "Executive Officer": 1, "Lt. Commander": 2, "Commander": 3,
  "Officer": 4, "Specialist": 5, "Crew": 6, "Member": 7,
};

// Get per-team sort order for a member
function getTeamSortOrder(member, teamName) {
  if (member.sort_orders && member.sort_orders[teamName] != null) return member.sort_orders[teamName];
  return member.sort_order ?? null;
}

function sortMembersForTeam(members, teamName) {
  return [...members].sort((a, b) => {
    const ra = getTeamRole(a, teamName);
    const rb = getTeamRole(b, teamName);
    const pa = ROLE_PRIORITY[ra] ?? 99;
    const pb = ROLE_PRIORITY[rb] ?? 99;
    if (pa !== pb) return pa - pb;
    const sa = parseFloat(getTeamSortOrder(a, teamName));
    const sb = parseFloat(getTeamSortOrder(b, teamName));
    if (!isNaN(sa) && !isNaN(sb) && sa !== sb) return sa - sb;
    if (!isNaN(sa)) return -1;
    if (!isNaN(sb)) return 1;
    return (a.discord_username || "").localeCompare(b.discord_username || "");
  });
}

// Normalise attendees — handles both legacy [userId] and new [{user_id,...}] format
function normalizeAttendees(raw) {
  if (!raw || !Array.isArray(raw)) return [];
  const byId = {};
  raw.forEach(a => {
    if (typeof a === "string") {
      byId[a] = { user_id: a, username: "", avatar: "", role: "", group: "", rsvp_group: "", sort_order: null };
    } else {
      const uid = a.user_id || "";
      byId[uid] = {
        user_id: uid,
        username: a.username || "",
        avatar: a.avatar || "",
        role: a.role || "",
        group: a.group || "",
        rsvp_group: a.rsvp_group || "",
        sort_order: a.sort_order ?? null,
      };
    }
  });
  return Object.values(byId);
}

function isUserAttending(attendees, userId) {
  return normalizeAttendees(attendees).some(a => a.user_id === userId);
}

// ─── Reusable donut pie chart with click-to-expand member list ────────────────
// `groups` shape: [{ label, sublabel?, count, members: [profile, ...] }]
// Clicking a slice or legend row toggles that group's expanded panel below.
const PIE_PALETTE = ["#fcd657", "#4fc3f7", "#ef5350", "#34d399", "#a78bfa", "#ce93d8", "#f87171", "#facc15", "#60a5fa", "#f59e0b"];

function PieChartCard({ title, accentColor, groups, expanded, onSelect, emptyText }) {
  const total = groups.reduce((s, g) => s + g.count, 0);
  if (total === 0) {
    return (
      <div style={{ padding: 24, border: `1px solid ${accentColor}22`, background: `${accentColor}05` }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: accentColor, textTransform: "uppercase", marginBottom: 16 }}>{title}</div>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textTransform: "uppercase" }}>{emptyText || "No data"}</div>
      </div>
    );
  }
  // Build slice geometry
  let cumAngle = 0;
  const slices = groups.map((g, i) => {
    const angle = (g.count / total) * 360;
    const startAngle = cumAngle;
    cumAngle += angle;
    return { ...g, color: PIE_PALETTE[i % PIE_PALETTE.length], startAngle, angle };
  });
  function polarToCart(cx, cy, r, deg) {
    const rad = (deg - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }
  const expandedGroup = expanded ? slices.find(s => s.label === expanded) : null;

  return (
    <div style={{ padding: 24, border: `1px solid ${accentColor}22`, background: `${accentColor}05` }}>
      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: accentColor, textTransform: "uppercase", marginBottom: 16 }}>{title}</div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
        <svg width="160" height="160" viewBox="0 0 160 160" style={{ flexShrink: 0 }}>
          {slices.map((s, i) => {
            const isActive = expanded === s.label;
            const fill = s.color;
            if (s.angle >= 359.9) {
              return (
                <circle key={i} cx="80" cy="80" r="70" fill={fill} opacity={isActive ? 1 : (expanded ? 0.4 : 0.85)}
                  style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                  onClick={() => onSelect(isActive ? null : s.label)} />
              );
            }
            const s1 = polarToCart(80, 80, 70, s.startAngle);
            const s2 = polarToCart(80, 80, 70, s.startAngle + s.angle);
            const large = s.angle > 180 ? 1 : 0;
            return (
              <path key={i} d={`M80,80 L${s1.x},${s1.y} A70,70 0 ${large},1 ${s2.x},${s2.y} Z`}
                fill={fill}
                opacity={isActive ? 1 : (expanded ? 0.4 : 0.85)}
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                onClick={() => onSelect(isActive ? null : s.label)}>
                <title>{s.label} — {s.count}</title>
              </path>
            );
          })}
          <circle cx="80" cy="80" r="36" fill="#05070f" />
          <text x="80" y="80" textAnchor="middle" dominantBaseline="middle" fill="#e8eaf6" fontFamily="Orbitron" fontSize="20" fontWeight="900">{total}</text>
        </svg>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 180 }}>
          {slices.map((s, i) => {
            const isActive = expanded === s.label;
            return (
              <button key={i} onClick={() => onSelect(isActive ? null : s.label)}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 8,
                  background: isActive ? `${s.color}15` : "transparent",
                  border: `1px solid ${isActive ? s.color : "transparent"}`,
                  padding: "6px 8px", cursor: "pointer", textAlign: "left",
                  transition: "all 0.15s",
                }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0, marginTop: 4 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: "#e8eaf6" }}>{s.label}</span>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)" }}>({s.count})</span>
                  </div>
                  {s.sublabel && (
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 0.5, marginTop: 1 }}>{s.sublabel}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Expanded member list panel */}
      {expandedGroup && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${expandedGroup.color}33` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 10 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: expandedGroup.color, textTransform: "uppercase" }}>
              {expandedGroup.label} · {expandedGroup.members.length} {expandedGroup.members.length === 1 ? "member" : "members"}
            </div>
            <button onClick={() => onSelect(null)} style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "rgba(232,234,246,0.45)", background: "transparent", border: "1px solid rgba(255,255,255,0.12)", padding: "3px 8px", cursor: "pointer", textTransform: "uppercase" }}>Close</button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {expandedGroup.members.map(m => (
              <Link key={m.id} to={`/profile/${m.id}`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "5px 10px",
                  border: `1px solid ${expandedGroup.color}33`,
                  background: `${expandedGroup.color}08`,
                  fontFamily: "'Rajdhani', sans-serif", fontSize: 12, fontWeight: 600, color: "#e8eaf6",
                  textDecoration: "none", transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = `${expandedGroup.color}1a`; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${expandedGroup.color}08`; }}>
                {m.discord_avatar && (
                  <img src={m.discord_avatar} alt="" style={{ width: 16, height: 16, borderRadius: "50%", flexShrink: 0 }} loading="lazy" />
                )}
                {m.discord_username || "Unknown"}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TeamPage() {
  const { teamSlug } = useParams();
  const navigate = useNavigate();
  const { user, profile, isAdmin } = useAuth();
  const { teams: preloadedTeams, profiles: preloadedProfiles, events: preloadedEvents, ships: preloadedShips, vacancies: preloadedVacancies, loaded: preloaded, refreshTeams, refreshEvents } = useFleetData();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [registryShips, setRegistryShips] = useState([]);
  // Status + search filter for the team fleet section. View-only.
  const [teamFleetFilter, setTeamFleetFilter] = useState({ status: 'all', search: '' });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("members");
  const [barsVisible, setBarsVisible] = useState(false);
  const [activeOp, setActiveOp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventSaving, setEventSaving] = useState(false);
  const [userReminders, setUserReminders] = useState({}); // { [event_id]: [{ type, remind_at }] }
  const [toast, setToast] = useState(null); // { message, type: "success"|"error" }
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyPositions, setApplyPositions] = useState([]);
  const [applyMessage, setApplyMessage] = useState("");
  const [applySubmitting, setApplySubmitting] = useState(false);

  // Calendar/list view state for the events tab — defaults to calendar.
  const [eventsViewMode, setEventsViewMode] = useState("calendar"); // "calendar" | "list"
  const [eventsCalendarMonth, setEventsCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [eventsSelectedDay, setEventsSelectedDay] = useState(null);

  // Which pie slice is expanded (shows its member list). Keyed by chart id.
  const [expandedHomeBase, setExpandedHomeBase] = useState(null);   // label string or null
  const [expandedTimezone, setExpandedTimezone] = useState(null);   // label string or null

  useEffect(() => {
    if (activeTab === "homes") setTimeout(() => setBarsVisible(true), 100);
    else setBarsVisible(false);
  }, [activeTab]);

  // Use preloaded data when available, otherwise fall back to direct fetch
  useEffect(() => {
    if (preloaded) {
      loadTeamFromCache();
    } else {
      loadTeamDirect();
    }
  }, [teamSlug, preloaded, preloadedTeams, preloadedProfiles, preloadedEvents, preloadedShips, preloadedVacancies]);

  function loadTeamFromCache() {
    const matched = preloadedTeams?.find(t => slugify(t.name) === teamSlug);
    if (!matched) { setNotFound(true); setLoading(false); return; }
    setTeam(matched);

    // Filter members from preloaded profiles
    const seen = new Set();
    const teamMembers = (preloadedProfiles || []).filter(p => {
      if (p.member_status === "left") return false; // exclude members who left the server
      const memberTeams = Array.isArray(p.teams) ? p.teams : (p.team ? [p.team] : []);
      return memberTeams.includes(matched.name) || p.team === matched.name;
    }).filter(member => {
      if (seen.has(member.id)) return false;
      seen.add(member.id);
      return true;
    });
    setMembers(sortMembersForTeam(teamMembers, matched.name));

    // Filter events for this team + org-wide (exclude archived)
    const teamEvents = (preloadedEvents || []).filter(e => !e.archived && (e.team === matched.name || e.is_org_wide));
    setEvents(teamEvents);

    // Filter vacancies for this team
    setVacancies((preloadedVacancies || []).filter(v => v.team_id === matched.id));
    setRegistryShips(preloadedShips || []);
    setLoading(false);
  }

  async function loadTeamDirect() {
    setLoading(true);
    const { data: teams } = await supabase.from("teams").select("*");
    const matched = teams?.find(t => slugify(t.name) === teamSlug);

    if (!matched) { setNotFound(true); setLoading(false); return; }
    setTeam(matched);

    const { data: m } = await supabase
      .from("profiles")
      .select("*")
      .or(`teams.cs.{${matched.name}},team.eq.${matched.name}`);

    const seen = new Set();
    const deduped = (m || []).filter(member => {
      if (member.member_status === "left") return false; // exclude members who left the server
      if (seen.has(member.id)) return false;
      seen.add(member.id);
      return true;
    });

    setMembers(sortMembersForTeam(deduped, matched.name));

    const { data: ev } = await supabase
      .from("events")
      .select("*")
      .eq("archived", false)
      .or(`team.eq.${matched.name},is_org_wide.eq.true`)
      .order("date");
    setEvents(ev || []);

    const [{ data: vac }, { data: shipsRegistry }] = await Promise.all([
      supabase.from("vacancies").select("*").eq("team_id", matched.id),
      supabase.from("ships").select(SHIPS_LIST_COLS).order("name"),
    ]);
    setVacancies(vac || []);
    setRegistryShips(shipsRegistry || []);

    setLoading(false);
  }

  function slugify(name) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function showToast(message, type = "success") {
    setToast({ message, type });
  }

  async function submitApplication() {
    if (!profile || !team) return;
    setApplySubmitting(true);
    const { data: app, error } = await supabase.from("team_applications").insert([{
      user_id: profile.id,
      team_id: team.id,
      selected_positions: applyPositions,
      message: applyMessage.trim(),
      status: "pending",
    }]).select().single();

    if (error) {
      showToast("Failed to submit application. Please try again.", "error");
      setApplySubmitting(false);
      return;
    }

    // Send Discord DM to team lead and co-lead
    const leadIds = [team.lead_id, team.co_lead_id].filter(Boolean);
    if (leadIds.length > 0) {
      const { data: leadProfiles } = await supabase
        .from("profiles")
        .select("discord_id")
        .in("id", leadIds);
      const leadDiscordIds = (leadProfiles || []).map(p => p.discord_id).filter(Boolean);
      for (const discordId of leadDiscordIds) {
        try {
          await fetch("/api/discord-notify", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "send_application_dm",
              discord_id: discordId,
              applicant_name: profile.discord_username,
              team_name: team.name,
              positions: applyPositions,
              message: applyMessage.trim(),
              application_id: app.id,
            }),
          });
        } catch (e) { /* non-critical */ }
      }
    }

    showToast("Application submitted! Leadership will be in touch.", "success");
    setShowApplyModal(false);
    setApplyPositions([]);
    setApplyMessage("");
    setApplySubmitting(false);
  }

  // Load user's reminder subscriptions for events on this team page
  useEffect(() => {
    if (!profile) return;
    async function loadReminders() {
      try {
        const { data } = await supabase
          .from("event_reminders")
          .select("event_id, reminder_type, remind_at")
          .eq("user_id", profile.id)
          .eq("sent", false);
        const map = {};
        (data || []).forEach(r => {
          if (!map[r.event_id]) map[r.event_id] = [];
          map[r.event_id].push({ type: r.reminder_type, remind_at: r.remind_at });
        });
        setUserReminders(map);
      } catch (e) { /* table may not exist yet */ }
    }
    loadReminders();
  }, [profile, events]);

  // 30-second polling for live attendee updates
  useEffect(() => {
    if (!team) return;
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from("events")
        .select("*")
        .or(`team.eq.${team.name},is_org_wide.eq.true`)
        .order("date");
      if (data) setEvents(data);
    }, 30000);
    return () => clearInterval(interval);
  }, [team]);

  // Toggle reminder subscription for a team event — individual types or clear-all
  async function toggleTeamReminder(event, reminderType, isRemoving) {
    if (!profile || !profile.discord_id) return;

    if (isRemoving) {
      try {
        await fetch("/api/event-reminders", {
          method: "DELETE", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_id: event.id, user_id: profile.id,
            ...(reminderType ? { reminder_type: reminderType } : {}),
          }),
        });
        if (reminderType) {
          setUserReminders(prev => {
            const existing = (prev[event.id] || []).filter(r => r.type !== reminderType);
            if (existing.length === 0) { const next = { ...prev }; delete next[event.id]; return next; }
            return { ...prev, [event.id]: existing };
          });
          showToast("Reminder removed", "success");
        } else {
          setUserReminders(prev => { const next = { ...prev }; delete next[event.id]; return next; });
          showToast("All reminders cleared", "success");
        }
      } catch (e) { /* non-critical */ }
    } else {
      try {
        const res = await fetch("/api/event-reminders", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_id: event.id, user_id: profile.id,
            discord_id: profile.discord_id,
            event_date: event.date, event_time: event.time || "00:00",
            ...(reminderType ? { reminder_type: reminderType } : {}),
          }),
        });
        const data = await res.json().catch(() => null);
        const newReminders = (data?.reminders || []).map(r => ({ type: r.reminder_type, remind_at: r.remind_at }));
        setUserReminders(prev => {
          const existing = prev[event.id] || [];
          const merged = [...existing];
          for (const nr of newReminders) { if (!merged.some(m => m.type === nr.type)) merged.push(nr); }
          return { ...prev, [event.id]: merged };
        });
        const labels = { at_start: "on event start", "10min": "10 minutes before", "1hr": "1 hour before", "24hr": "1 day before", "1week": "1 week before" };
        showToast(`🔔 Reminder set — ${labels[reminderType] || "before this event"}`, "success");
      } catch (e) { /* non-critical */ }
    }
  }

  if (loading) return (
    <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: "rgba(206,147,216,0.6)" }}>
      LOADING...
    </div>
  );

  if (notFound) return (
    <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
      <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 48, color: "rgba(255,255,255,0.1)" }}>404</div>
      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 3, color: "rgba(255,255,255,0.3)" }}>TEAM NOT FOUND</div>
      <Link to="/hierarchy" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, color: ACCENT, textDecoration: "none", border: `1px solid ${ACCENT}44`, padding: "8px 20px" }}>← Back to Hierarchy</Link>
    </div>
  );

  const accent = team.color || ACCENT;
  const teamName = team.name;

  // Group members by per-team role for leadership/crew split
  const leaders = members.filter(m => (ROLE_PRIORITY[getTeamRole(m, teamName)] ?? 99) <= 3);
  const crew = members.filter(m => (ROLE_PRIORITY[getTeamRole(m, teamName)] ?? 99) > 3);

  // Home base groups for the pie chart — one slice per location, with the member
  // list captured so a slice can expand to show who's there.
  const homeBaseGroups = (() => {
    const map = {};
    members.forEach(m => {
      const key = m.home_base || "Undisclosed";
      if (!map[key]) map[key] = { label: key, count: 0, members: [] };
      map[key].count++;
      map[key].members.push(m);
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  })();

  // Timezone groups merged by current UTC offset (so Toronto + New York share an
  // "Eastern" slice). Sublabel keeps the per-city breakdown so regional info isn't lost.
  const timezoneGroups = (() => {
    function offsetLabel(off) {
      const friendly = {
        "-10": "Hawaii", "-9": "Alaska", "-8": "Pacific", "-7": "Mountain", "-6": "Central",
        "-5": "Eastern", "-4": "Atlantic", "-3.5": "Newfoundland", "-3": "Brasília",
        "0": "UTC", "1": "Central European", "2": "Eastern European", "3": "Moscow",
        "5.5": "India", "8": "China", "9": "Japan", "9.5": "Aus. Central", "10": "Aus. Eastern",
      }[String(off)];
      const sign = off >= 0 ? "+" : "−";
      const abs = Math.abs(off);
      const intP = Math.floor(abs);
      const fracP = abs - intP;
      const fracStr = fracP === 0 ? "" : fracP === 0.5 ? ":30" : fracP === 0.25 ? ":15" : fracP === 0.75 ? ":45" : "";
      const utc = `UTC${sign}${intP}${fracStr}`;
      return friendly ? `${friendly} (${utc})` : utc;
    }
    const groups = {}; // key = offset (or "unset"), value = { label, sublabel, count, cities: { city: count }, members }
    members.forEach(m => {
      if (!m.timezone) {
        if (!groups.unset) groups.unset = { label: "Not Set", sublabel: null, count: 0, cities: {}, members: [] };
        groups.unset.count++;
        groups.unset.members.push(m);
        return;
      }
      const off = getOffsetHours(m.timezone);
      const city = m.timezone.replace(/_/g, " ").replace(/^.*\//, "");
      const key = String(off);
      if (!groups[key]) groups[key] = { label: offsetLabel(off), sublabel: null, count: 0, cities: {}, members: [] };
      groups[key].count++;
      groups[key].cities[city] = (groups[key].cities[city] || 0) + 1;
      groups[key].members.push(m);
    });
    return Object.values(groups).map(g => {
      const cities = Object.entries(g.cities).sort((a, b) => b[1] - a[1]);
      const sublabel = cities.length > 1
        ? cities.map(([c, n]) => `${c} · ${n}`).join(", ")
        : (cities.length === 1 ? cities[0][0] : null);
      return { ...g, sublabel };
    }).sort((a, b) => b.count - a.count);
  })();

  const availableMembers = members.filter(m => m.availability && Object.keys(m.availability).length > 0);

  // Compute the team's peak activity hours per weekday — used by the calendar's peak-hour indicator.
  // Times are converted into the viewer's timezone so the indicator lines up with how they'd schedule.
  const peakHoursByDow = (() => {
    if (availableMembers.length === 0) return null;
    const viewerTz = profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const viewerOffset = getOffsetHours(viewerTz);

    function normalizeWindows(val) {
      if (!val) return [];
      if (Array.isArray(val)) return val.filter(w => w && typeof w.start === 'number' && typeof w.end === 'number');
      if (typeof val === 'object' && val.all_day) return [{ start: 0, end: 24 }];
      if (typeof val === 'object' && typeof val.start === 'number' && typeof val.end === 'number') return [val];
      return [];
    }

    const result = {};
    DAYS.forEach(day => {
      // Per-hour count of available members across the day in viewer's TZ
      const counts = new Array(24).fill(0);
      availableMembers.forEach(member => {
        const memberOffset = getOffsetHours(member.timezone || "UTC");
        const shift = viewerOffset - memberOffset;
        const wins = normalizeWindows(member.availability?.[day]);
        wins.forEach(win => {
          if (win.start === 0 && win.end === 24) {
            for (let h = 0; h < 24; h++) counts[h]++;
            return;
          }
          let s = ((win.start + shift) % 24 + 24) % 24;
          let e = ((win.end + shift) % 24 + 24) % 24;
          if (e <= s) e += 24;
          for (let h = Math.floor(s); h < Math.ceil(e); h++) counts[h % 24]++;
        });
      });
      const max = Math.max(...counts);
      if (max === 0) { result[day] = null; return; }
      // Find the longest contiguous range hitting peak count.
      let bestStart = 0, bestLen = 0, curStart = -1, curLen = 0;
      for (let h = 0; h < 24; h++) {
        if (counts[h] === max) {
          if (curStart === -1) curStart = h;
          curLen++;
          if (curLen > bestLen) { bestStart = curStart; bestLen = curLen; }
        } else { curStart = -1; curLen = 0; }
      }
      result[day] = { start: bestStart, end: bestStart + bestLen, count: max };
    });
    return result;
  })();

  // Split events into upcoming and past for team
  const now = new Date();
  const upcomingTeamEvents = events.filter(e => !e.archived && (!e.date || new Date(`${e.date}T${e.time || "23:59"}`) >= now));
  const pastTeamEvents = events.filter(e => !e.archived && e.date && new Date(`${e.date}T${e.time || "23:59"}`) < now);

  // Toggle RSVP for team events
  async function toggleTeamRSVP(event, selectedOption) {
    if (!profile) return;
    const attendees = normalizeAttendees(event.attendees);
    const existing = attendees.find(a => a.user_id === profile.id);
    let updated;

    const isLeaving = (selectedOption === null && existing) || (selectedOption && !selectedOption.attending);
    const isJoining = selectedOption && selectedOption.attending;

    if (isLeaving) {
      updated = attendees.filter(a => a.user_id !== profile.id);
      try {
        await fetch("/api/event-reminders", {
          method: "DELETE", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ event_id: event.id, user_id: profile.id }),
        });
        setUserReminders(prev => { const next = { ...prev }; delete next[event.id]; return next; });
      } catch (e) { /* non-critical */ }
    } else if (isJoining) {
      if (existing) {
        updated = attendees.map(a => a.user_id === profile.id ? { ...a, rsvp_group: selectedOption.name } : a);
      } else {
        updated = [...attendees, {
          user_id: profile.id,
          username: profile.discord_username || "",
          avatar: profile.discord_avatar || "",
          role: "", group: "",
          rsvp_group: selectedOption.name,
          sort_order: null,
        }];
        if (selectedOption.reminders && profile.discord_id && event.date) {
          try {
            const remRes = await fetch("/api/event-reminders", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ event_id: event.id, user_id: profile.id, discord_id: profile.discord_id, event_date: event.date, event_time: event.time || "00:00" }),
            });
            const remData = await remRes.json().catch(() => null);
            const schedule = (remData?.reminders || []).map(r => ({ type: r.reminder_type, remind_at: r.remind_at }));
            setUserReminders(prev => ({ ...prev, [event.id]: schedule.length > 0 ? schedule : [{ type: "pending" }] }));
          } catch (e) { /* non-critical */ }
        }
        // Send signup confirmation DM with reminder buttons
        if (profile.discord_id) {
          try {
            await fetch("/api/discord-notify", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "send_dm", discord_id: profile.discord_id, event, dm_type: "signup_confirm" }),
            });
          } catch (e) { /* non-critical */ }
        }
      }
    } else {
      // Simple toggle fallback (no rsvp_options)
      if (existing) {
        updated = attendees.filter(a => a.user_id !== profile.id);
        try {
          await fetch("/api/event-reminders", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ event_id: event.id, user_id: profile.id }) });
          setUserReminders(prev => { const next = { ...prev }; delete next[event.id]; return next; });
        } catch (e) { /* non-critical */ }
      } else {
        updated = [...attendees, { user_id: profile.id, username: profile.discord_username || "", avatar: profile.discord_avatar || "", role: "", group: "", sort_order: null }];
        if (profile.discord_id && event.date) {
          try {
            const remRes = await fetch("/api/event-reminders", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ event_id: event.id, user_id: profile.id, discord_id: profile.discord_id, event_date: event.date, event_time: event.time || "00:00" }) });
            const remData = await remRes.json().catch(() => null);
            const schedule = (remData?.reminders || []).map(r => ({ type: r.reminder_type, remind_at: r.remind_at }));
            setUserReminders(prev => ({ ...prev, [event.id]: schedule.length > 0 ? schedule : [{ type: "pending" }] }));
          } catch (e) { /* non-critical */ }
        }
        // Send signup confirmation DM with reminder buttons
        if (profile.discord_id) {
          try {
            await fetch("/api/discord-notify", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "send_dm", discord_id: profile.discord_id, event, dm_type: "signup_confirm" }),
            });
          } catch (e) { /* non-critical */ }
        }
      }
    }

    await supabase.from("events").update({ attendees: updated }).eq("id", event.id);
    await refreshEvents();

    // Update all Discord embeds with latest RSVP roster
    try {
      fetch("/api/discord-notify", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_rsvp_embed", event_id: event.id }),
      });
    } catch (e) { /* non-critical */ }
  }


  // Convert event date/time from creator's timezone to viewer's profile timezone
  function convertEventDate(ev) {
    const evDate = ev.date ? new Date(`${ev.date}T${ev.time || "00:00"}`) : null;
    if (!evDate || !ev.time) return { date: evDate, converted: false };
    const viewerTz = profile?.timezone || null;
    const creatorTz = ev.creator_timezone || null;
    if (!viewerTz || !creatorTz || viewerTz === creatorTz) return { date: evDate, converted: false };
    try {
      const cOff = new Date(evDate.toLocaleString("en-US", { timeZone: creatorTz })).getTime();
      const vOff = new Date(evDate.toLocaleString("en-US", { timeZone: viewerTz })).getTime();
      return { date: new Date(evDate.getTime() + (vOff - cOff)), converted: true };
    } catch { return { date: evDate, converted: false }; }
  }

  // Check if user can create events for this team (lead, co-lead, or admin)
  const isTeamLeadOrCoLead = team && (team.lead_id === profile?.id || team.co_lead_id === profile?.id);
  const canCreateTeamEvent = profile && (
    isAdmin ||
    profile.perm_super_admin ||
    profile.perm_division_lead ||
    (profile.perm_team_lead && profile.managed_team === team?.name) ||
    isTeamLeadOrCoLead ||
    (profile.org_role_sort && profile.org_role_sort <= 2.9)
  );

  async function saveTeamEvent(formData) {
    if (!formData.title?.trim() || !formData.date || !team) return;
    setEventSaving(true);
    const payload = {
      ...formData,
      team: formData.is_org_wide ? null : team.name,
      created_by: profile.id,
      creator_timezone: profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      attendees: [],
    };
    const { data: inserted } = await supabase.from("events").insert([payload]).select();
    const newEvent = inserted?.[0];

    // Post to Discord channel (or test DM)
    if (newEvent && team.discord_channel_id) {
      try {
        if (team.discord_test_mode && team.discord_test_user_id) {
          // Test mode — DM the test user
          const res = await fetch("/api/discord-notify", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "send_dm",
              discord_id: team.discord_test_user_id,
              event: newEvent,
            }),
          });
          showToast(res.ok ? "✓ Test DM sent — new event" : "✗ Test DM failed to send", res.ok ? "success" : "error");
        } else {
          // Live mode — post to team Discord channel
          const res = await fetch("/api/discord-notify", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "post_event",
              channel_id: team.discord_channel_id,
              event: newEvent,
            }),
          });
          if (res.ok) {
            const result = await res.json().catch(() => null);
            if (result?.message_id) {
              await supabase.from("event_discord_posts").upsert({
                event_id: newEvent.id,
                channel_id: team.discord_channel_id,
                message_id: result.message_id,
                thread_id: result.thread_id || null,
              });
            }
            showToast("✓ Event posted to Discord channel", "success");
          } else {
            showToast("✗ Failed to post to Discord channel", "error");
          }
        }
      } catch (e) { console.warn("Discord post failed:", e); showToast("✗ Discord post failed", "error"); }
    }

    setEventSaving(false);
    setShowEventForm(false);
    setEditingEvent(null);
    await refreshEvents();
  }

  async function updateTeamEvent(formData) {
    if (!editingEvent) return;
    setEventSaving(true);
    const payload = { ...formData };
    delete payload.id; delete payload.created_at; delete payload.attendees;
    await supabase.from("events").update(payload).eq("id", editingEvent.id);
    setEventSaving(false);
    setShowEventForm(false);
    setEditingEvent(null);
    await refreshEvents();
    showToast("✓ Event updated", "success");
  }

  function canEditEvent(ev) {
    if (!profile) return false;
    if (isAdmin || profile.perm_super_admin) return true;
    if (profile.perm_division_lead) return true;
    if (ev.team && (profile.perm_team_lead && profile.managed_team === ev.team)) return true;
    if (isTeamLeadOrCoLead) return true;
    if (ev.created_by === profile.id) return true;
    return false;
  }

  async function archiveTeamEvent(eventId) {
    if (!window.confirm("Archive this event?")) return;
    await supabase.from("events").update({ archived: true }).eq("id", eventId);
    await refreshEvents();
    showToast("✓ Event archived", "success");
  }

  const TABS = [
    { id: "members", label: "Team Members", color: accent },
    { id: "fleet", label: "Team Fleet", color: "#a78bfa" },
    { id: "homes", label: "Home Bases", color: "#34d399" },
    ...(availableMembers.length > 0 ? [{ id: "availability", label: "Team Availability", color: "#f59e0b" }] : []),
    { id: "events", label: "Events", color: "#f87171" },
  ];

  const typeColors = { Combat: "#ef5350", Industry: "#fcd657", Salvage: "#a78bfa", Training: "#4fc3f7", Piracy: "#f87171", Exploration: "#34d399", Other: "#ce93d8" };

  return (
    <div style={{ fontFamily: "'Rajdhani', 'Orbitron', sans-serif", background: "#05070f", color: "#e8eaf6", minHeight: "100vh", overflowX: "hidden" }}>
      <StarField />

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: "fixed", top: 80, right: 24, zIndex: 9999,
          padding: "14px 24px", borderRadius: 4,
          background: toast.type === "error" ? "rgba(244,67,54,0.95)" : "rgba(76,175,80,0.95)",
          color: "#fff", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600,
          letterSpacing: 0.5, boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
          animation: "toastSlideIn 0.3s ease-out",
          display: "flex", alignItems: "center", gap: 10, maxWidth: 380,
          cursor: "pointer",
        }} onClick={() => setToast(null)}>
          <span>{toast.message}</span>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: ${accent}; color: #05070f; }
        a { text-decoration: none; color: inherit; }
        .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); transition: color 0.2s; padding: 4px 0; position: relative; }
        .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: ${accent}; transition: width 0.3s ease; }
        .nav-link:hover { color: ${accent}; }
        .nav-link:hover::after { width: 100%; }
        .glow-btn { display: inline-block; padding: 12px 32px; border: 2px solid ${accent}; color: ${accent}; font-family: 'Orbitron', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; cursor: pointer; background: transparent; transition: all 0.3s ease; clip-path: polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%); }
        .glow-btn:hover { background: ${accent}; color: #05070f; box-shadow: 0 0 40px ${accent}55; }
        .scanline { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px); }
        @keyframes floatUp { from { opacity: 0; transform: translateY(50px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes toastSlideIn { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
      `}</style>
      <div className="scanline" />
      <Nav accentColor={accent} />

      {/* HERO */}
      <section style={{ position: "relative", minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", padding: "120px 40px 80px" }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 700, background: `radial-gradient(circle, ${accent}12 0%, transparent 70%)`, pointerEvents: "none", zIndex: 1 }} />
        {team.patch_url && (
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 500, height: 500, backgroundImage: `url(${team.patch_url})`, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center", opacity: 0.05, zIndex: 1, pointerEvents: "none" }} />
        )}

        <div style={{ position: "relative", zIndex: 2, maxWidth: 860, width: "100%", textAlign: "center" }}>
          <div style={{ opacity: 0, animation: "floatUp 0.6s ease forwards 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 28 }}>
            <Link to="/hierarchy" style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 4, color: `${accent}88`, transition: "color 0.2s" }}
              onMouseEnter={e => e.target.style.color = accent} onMouseLeave={e => e.target.style.color = `${accent}88`}>
              {toArray(team.divisions).length > 0 ? toArray(team.divisions).join(" · ").toUpperCase() : (team.division?.toUpperCase() || "SECURITY")} DIVISION
            </Link>
            <span style={{ color: "rgba(255,255,255,0.2)" }}>◆</span>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 4, color: accent }}>TEAM</span>
          </div>

          {team.patch_url && (
            <div style={{ width: 160, height: 160, margin: "0 auto 28px", opacity: 0, animation: "floatUp 0.7s ease forwards 0.3s" }}>
              <img src={team.patch_url} alt={`${team.name} patch`} style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
            </div>
          )}

          <div style={{ opacity: 0, animation: "floatUp 0.7s ease forwards 0.4s" }}>
            <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(32px, 7vw, 84px)", lineHeight: 0.92, color: "#e8eaf6", marginBottom: 24,
              background: `linear-gradient(90deg, #e8eaf6, ${accent}, #e8eaf6)`, backgroundSize: "200% auto",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmer 5s linear infinite" }}>
              {team.name.toUpperCase()}
            </h1>
          </div>

          {team.tagline && (
            <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: "clamp(15px, 2vw, 20px)", fontWeight: 600, color: "rgba(232,234,246,0.65)", lineHeight: 1.6, opacity: 0, animation: "floatUp 0.7s ease forwards 0.55s" }}>
              {team.tagline}
            </p>
          )}

          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", marginTop: 36, opacity: 0, animation: "floatUp 0.7s ease forwards 0.7s" }}>
            <button className="glow-btn" onClick={() => { setApplyPositions([]); setApplyMessage(""); setShowApplyModal(true); }}>Apply to Join</button>
            <Link to="/hierarchy?tab=divisions" style={{ display: "inline-block", padding: "12px 32px", border: "2px solid rgba(255,255,255,0.18)", color: "rgba(232,234,246,0.55)", fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", transition: "all 0.3s ease", clipPath: "polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%)" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)"; e.currentTarget.style.color = "#e8eaf6"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; e.currentTarget.style.color = "rgba(232,234,246,0.55)"; }}>
              All Teams
            </Link>
          </div>
        </div>

        <div style={{ position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, opacity: 0, animation: "floatUp 0.6s ease forwards 1.1s" }}>
          <span style={{ fontFamily: "'Space Mono'", fontSize: 9, letterSpacing: 3, color: "rgba(255,255,255,0.22)" }}>SCROLL</span>
          <div style={{ width: 1, height: 36, background: `linear-gradient(to bottom, ${accent}77, transparent)`, animation: "pulse 2s infinite" }} />
        </div>
      </section>

      {/* ABOUT */}
      {(team.about || team.description) && (
        <section style={{ position: "relative", zIndex: 1, borderTop: `1px solid ${accent}18`, background: "rgba(18,9,24,0.55)", padding: "80px 40px" }}>
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <Reveal>
              <SectionLabel color={accent}>About</SectionLabel>
              <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(22px, 4vw, 40px)", color: "#e8eaf6", marginBottom: 32, lineHeight: 1.1 }}>
                {team.name}
              </h2>
            </Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 32 }}>
              {(team.about || team.description || "").split("\n\n").filter(p => p.trim()).map((para, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 17, color: "rgba(232,234,246,0.62)", lineHeight: 1.9 }}>{para}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OPERATIONS */}
      {team.operations && team.operations.length > 0 && (
        <section style={{ position: "relative", zIndex: 1, padding: "80px 40px" }}>
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <Reveal>
              <SectionLabel color={accent}>Mission Profile</SectionLabel>
              <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(22px, 4vw, 36px)", color: "#e8eaf6", marginBottom: 12 }}>What We Do</h2>
              <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 2, marginBottom: 28 }}>CLICK AN OPERATION TO SEE INTERESTED MEMBERS</p>
            </Reveal>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {team.operations.map((op, i) => {
                const opDef = ALL_OPERATIONS.find(o => o.label === op);
                const opColor = opDef?.color || accent;
                const opIcon = opDef?.icon || "◆";
                const isActive = activeOp === op;
                return (
                  <Reveal key={op} delay={i * 0.07}>
                    <div
                      onClick={() => setActiveOp(isActive ? null : op)}
                      style={{ padding: "16px 24px", border: `1px solid ${isActive ? opColor : opColor + "44"}`, background: isActive ? opColor + "22" : opColor + "08", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: isActive ? opColor : opColor + "bb", textTransform: "uppercase", transition: "all 0.2s ease", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, boxShadow: isActive ? `0 0 20px ${opColor}33` : "none" }}>
                      <span style={{ fontSize: 16 }}>{opIcon}</span>
                      {op}
                      {isActive && (() => {
                        const interested = members.filter(m => Array.isArray(m.interests) && m.interests.includes(op));
                        return interested.length > 0
                          ? <span style={{ fontFamily: "'Orbitron', monospace", fontSize: 8, padding: "1px 6px", background: opColor, color: "#05070f", borderRadius: 10 }}>{interested.length}</span>
                          : null;
                      })()}
                    </div>
                  </Reveal>
                );
              })}
            </div>
            {activeOp && (() => {
              const opDef = ALL_OPERATIONS.find(o => o.label === activeOp);
              const opColor = opDef?.color || accent;
              const interested = members.filter(m => Array.isArray(m.interests) && m.interests.includes(activeOp));
              return (
                <div style={{ marginTop: 32, padding: "20px 24px", border: `1px solid ${opColor}33`, background: `${opColor}08` }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: opColor, marginBottom: 16, textTransform: "uppercase" }}>
                    {opDef?.icon} Members interested in {activeOp} ({interested.length})
                  </div>
                  {interested.length === 0 ? (
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.3)" }}>No members have listed this as an interest yet.</div>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {interested.map(m => (
                        <Link key={m.id} to={`/profile/${m.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", border: `1px solid ${opColor}44`, background: `${opColor}12`, borderRadius: 2, transition: "all 0.2s", cursor: "pointer" }}
                            onMouseEnter={e => { e.currentTarget.style.background = `${opColor}20`; e.currentTarget.style.borderColor = `${opColor}66`; }}
                            onMouseLeave={e => { e.currentTarget.style.background = `${opColor}12`; e.currentTarget.style.borderColor = `${opColor}44`; }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: `1px solid ${opColor}55` }}>
                              {m.discord_avatar
                                ? <img src={m.discord_avatar} alt={m.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                                : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 10, color: opColor }}>{m.discord_username?.charAt(0)}</div>
                              }
                            </div>
                            <div>
                              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{m.discord_username}<GuestBadge member={m} /></div>
                              {getTeamRole(m, teamName) && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: opColor }}>{getTeamRole(m, teamName)}</div>}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* TABBED SECTION */}
      <section style={{ position: "relative", zIndex: 1, background: "rgba(18,9,24,0.5)", borderTop: `1px solid ${accent}15`, borderBottom: `1px solid ${accent}15`, paddingBottom: 80 }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 40px" }}>
          <div style={{ display: "flex", gap: 2, borderBottom: "1px solid rgba(255,255,255,0.08)", marginBottom: 48, flexWrap: "wrap" }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", padding: "16px 22px", border: "none", cursor: "pointer", background: activeTab === tab.id ? `${tab.color}12` : "transparent", color: activeTab === tab.id ? tab.color : "rgba(232,234,246,0.38)", borderBottom: activeTab === tab.id ? `2px solid ${tab.color}` : "2px solid transparent", transition: "all 0.2s ease" }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* MEMBERS TAB */}
          {activeTab === "members" && (() => {
            const subGroups = team.sub_groups || [];
            const teamVacancies = vacancies;

            if (subGroups.length > 0) {
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
                  {subGroups.map((group, gi) => {
                    // Use per-team sub_group for each member
                    const groupMembers = sortMembersForTeam(
                      members.filter(m => getTeamSubGroup(m, teamName) === group.name),
                      teamName
                    );
                    const groupVacancies = teamVacancies.filter(v => v.sub_group === group.name);
                    if (groupMembers.length === 0 && groupVacancies.length === 0) return null;
                    return (
                      <Reveal key={group.name} delay={gi * 0.08}>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: group.color, textTransform: "uppercase", marginBottom: 14 }}>{group.name}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                          {groupMembers.map(m => {
                            const role = getTeamRole(m, teamName);
                            return (
                              <Link key={m.id} to={`/profile/${m.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", border: `1px solid ${group.color}44`, background: `${group.color}08`, borderRadius: 2, transition: "all 0.2s", cursor: "pointer" }}
                                  onMouseEnter={e => { e.currentTarget.style.background = `${group.color}15`; e.currentTarget.style.borderColor = `${group.color}66`; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = `${group.color}08`; e.currentTarget.style.borderColor = `${group.color}44`; }}>
                                  <Avatar src={m.discord_avatar} name={m.discord_username} size={38} color={group.color} />
                                  <div>
                                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6" }}>{m.discord_username}<GuestBadge member={m} /></div>
                                    {role && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: group.color }}>{role}</div>}
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                          {groupVacancies.map(v => (
                            Array.from({ length: v.quantity }).map((_, qi) => (
                              <div key={`${v.id}-${qi}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", border: `1px solid ${group.color}22`, background: "rgba(255,255,255,0.01)", borderRadius: 2, opacity: 0.55 }}>
                                <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.04)", border: `1px dashed ${group.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 16, color: "rgba(255,255,255,0.2)" }}>?</div>
                                <div>
                                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "rgba(232,234,246,0.35)", fontStyle: "italic" }}>Vacant</div>
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: group.color, opacity: 0.7 }}>{v.position}</div>
                                </div>
                              </div>
                            ))
                          ))}
                        </div>
                      </Reveal>
                    );
                  })}

                  {/* Ungrouped members — those with no sub_group for this team */}
                  {(() => {
                    const ungrouped = sortMembersForTeam(
                      members.filter(m => {
                        const sg = getTeamSubGroup(m, teamName);
                        return !sg || !subGroups.find(g => g.name === sg);
                      }),
                      teamName
                    );
                    const ungroupedVacancies = teamVacancies.filter(v => !subGroups.find(g => g.name === v.sub_group));
                    if (ungrouped.length === 0 && ungroupedVacancies.length === 0) return null;
                    return (
                      <Reveal delay={subGroups.length * 0.08}>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 14 }}>Unassigned</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                          {ungrouped.map(m => {
                            const role = getTeamRole(m, teamName);
                            return (
                              <Link key={m.id} to={`/profile/${m.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)", borderRadius: 2, transition: "all 0.2s", cursor: "pointer" }}
                                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}>
                                  <Avatar src={m.discord_avatar} name={m.discord_username} size={36} color="rgba(232,234,246,0.3)" />
                                  <div>
                                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{m.discord_username}<GuestBadge member={m} /></div>
                                    {role && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)" }}>{role}</div>}
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                          {ungroupedVacancies.map(v => (
                            Array.from({ length: v.quantity }).map((_, qi) => (
                              <div key={`${v.id}-${qi}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", border: `1px solid rgba(255,255,255,0.1)`, background: "rgba(255,255,255,0.01)", borderRadius: 2, opacity: 0.55 }}>
                                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.2)", fontSize: 16 }}>?</div>
                                <div>
                                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "rgba(232,234,246,0.35)", fontStyle: "italic" }}>Vacant</div>
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.3)" }}>{v.position}</div>
                                </div>
                              </div>
                            ))
                          ))}
                        </div>
                      </Reveal>
                    );
                  })()}
                </div>
              );
            }

            // No sub-groups — leadership / crew split
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
                {leaders.length > 0 && (
                  <div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: accent, textTransform: "uppercase", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 24, height: 1, background: accent }} /> Leadership ({leaders.length})
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {leaders.map((m, i) => {
                        const role = getTeamRole(m, teamName);
                        return (
                          <Reveal key={m.id} delay={i * 0.07}>
                            <Link to={`/profile/${m.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", border: `1px solid ${accent}44`, background: `${accent}08`, borderRadius: 2, transition: "all 0.2s", cursor: "pointer" }}
                                onMouseEnter={e => { e.currentTarget.style.background = `${accent}15`; e.currentTarget.style.borderColor = `${accent}66`; }}
                                onMouseLeave={e => { e.currentTarget.style.background = `${accent}08`; e.currentTarget.style.borderColor = `${accent}44`; }}>
                                <Avatar src={m.discord_avatar} name={m.discord_username} size={40} color={accent} />
                                <div>
                                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6" }}>{m.discord_username}<GuestBadge member={m} /></div>
                                  {role && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: accent }}>{role}</div>}
                                </div>
                              </div>
                            </Link>
                          </Reveal>
                        );
                      })}
                    </div>
                  </div>
                )}
                {crew.length > 0 && (
                  <div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 24, height: 1, background: "rgba(232,234,246,0.2)" }} /> Crew ({crew.length})
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {crew.map((m, i) => {
                        const role = getTeamRole(m, teamName);
                        return (
                          <Reveal key={m.id} delay={i * 0.05}>
                            <Link to={`/profile/${m.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)", borderRadius: 2, transition: "all 0.2s", cursor: "pointer" }}
                                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}>
                                <Avatar src={m.discord_avatar} name={m.discord_username} size={36} color="rgba(232,234,246,0.3)" />
                                <div>
                                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{m.discord_username}<GuestBadge member={m} /></div>
                                  {role && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "rgba(232,234,246,0.4)" }}>{role}</div>}
                                </div>
                              </div>
                            </Link>
                          </Reveal>
                        );
                      })}
                    </div>
                  </div>
                )}
                {vacancies.length > 0 && (
                  <Reveal>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: "rgba(232,234,246,0.3)", textTransform: "uppercase", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 24, height: 1, background: "rgba(232,234,246,0.15)" }} /> Open Positions
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {vacancies.map(v => Array.from({ length: v.quantity }).map((_, qi) => (
                        <div key={`${v.id}-${qi}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", border: "1px dashed rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.01)", borderRadius: 2, opacity: 0.55 }}>
                          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.2)", fontSize: 16 }}>?</div>
                          <div>
                            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "rgba(232,234,246,0.35)", fontStyle: "italic" }}>Vacant</div>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.3)" }}>{v.position}</div>
                          </div>
                        </div>
                      )))}
                    </div>
                  </Reveal>
                )}
                {members.length === 0 && vacancies.length === 0 && (
                  <div style={{ textAlign: "center", padding: 60, fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>NO MEMBERS ASSIGNED YET</div>
                )}
              </div>
            );
          })()}

          {/* FLEET TAB */}
          {activeTab === "fleet" && (() => {
            // Check if user can manage featured fleet (team lead+ or admin)
            const canManageFeatured = profile && (
              isAdmin ||
              profile.perm_super_admin ||
              profile.perm_division_lead ||
              (profile.perm_team_lead && profile.managed_team === team.name) ||
              team.lead_id === profile.id
            );

            // Team featured fleet — stored on teams.primary_ships, completely independent of user personal primaries
            const featuredShipIds = Array.isArray(team.primary_ships) ? team.primary_ships : [];

            async function toggleFeatured(shipId) {
              const updated = featuredShipIds.includes(shipId)
                ? featuredShipIds.filter(id => id !== shipId)
                : [...featuredShipIds, shipId];
              await supabase.from("teams").update({ primary_ships: updated }).eq("id", team.id);
              setTeam(prev => ({ ...prev, primary_ships: updated }));
            }

            // Aggregate ships across team members — respect the status filter
            // so the counts shown match what's actually being filtered.
            const shipCounts = {};
            members.forEach(m => {
              const userShips = Array.isArray(m.ships) ? m.ships : [];
              userShips.forEach(entry => {
                if (typeof entry === "object" && entry !== null && entry.ship_id) {
                  if (teamFleetFilter.status !== 'all' && getShipStatus(entry) !== teamFleetFilter.status) return;
                  if (!shipCounts[entry.ship_id]) shipCounts[entry.ship_id] = { owners: 0, totalCount: 0 };
                  shipCounts[entry.ship_id].owners += 1;
                  shipCounts[entry.ship_id].totalCount += (entry.quantity || 1);
                }
              });
            });

            // Filter by search (name or manufacturer) on top of status aggregation.
            const searchQ = (teamFleetFilter.search || '').trim().toLowerCase();
            const teamShipsUnsorted = registryShips.filter(s => {
              if (!shipCounts[s.id]) return false;
              if (searchQ) {
                const hay = `${s.name || ''} ${s.manufacturer || ''} ${s.category || ''}`.toLowerCase();
                if (!hay.includes(searchQ)) return false;
              }
              return true;
            });
            const teamShipsBeforeFilter = registryShips.filter(s => shipCounts[s.id]);

            const teamShips = teamShipsUnsorted
              .sort((a, b) => {
                const aFeat = featuredShipIds.includes(a.id) ? 1 : 0;
                const bFeat = featuredShipIds.includes(b.id) ? 1 : 0;
                if (bFeat !== aFeat) return bFeat - aFeat;
                return (shipCounts[b.id]?.totalCount || 0) - (shipCounts[a.id]?.totalCount || 0);
              });

            const totalShips = Object.values(shipCounts).reduce((s, d) => s + d.totalCount, 0);
            const hasFeatured = teamShips.some(s => featuredShipIds.includes(s.id));

            return (
              <Reveal>
                <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: "rgba(232,234,246,0.4)", marginBottom: 8 }}>
                  {teamShips.length} ship types — {totalShips} total across {members.length} members.
                </p>
                <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.25)", letterSpacing: 1, marginBottom: 16 }}>
                  {canManageFeatured ? "Click ◆ to feature ships for this team — click any card for full details" : "Click any ship for full details"}
                </p>

                <FleetFilterBar
                  value={teamFleetFilter}
                  onChange={setTeamFleetFilter}
                  totalCount={teamShips.length === teamShipsBeforeFilter.length ? teamShips.length : `${teamShips.length}/${teamShipsBeforeFilter.length}`}
                />

                {teamShipsBeforeFilter.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 60, fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>
                    NO SHIPS REGISTERED BY TEAM MEMBERS
                  </div>
                ) : teamShips.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.35)", letterSpacing: 2 }}>
                    NO SHIPS MATCH THE CURRENT FILTER
                  </div>
                ) : (
                  <div>
                    {/* Featured fleet — larger cards with cyan accent (distinct from user gold primary) */}
                    {hasFeatured && (
                      <div style={{ marginBottom: 24 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                          <span style={{ fontSize: 14, color: "#4fc3f7" }}>◆</span>
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#4fc3f7", textTransform: "uppercase" }}>Featured Fleet</span>
                          <div style={{ flex: 1, height: 1, background: "rgba(79,195,247,0.2)" }} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
                          {teamShips.filter(s => featuredShipIds.includes(s.id)).map(ship => {
                            const data = shipCounts[ship.id] || { owners: 0, totalCount: 0 };
                            return (
                              <div key={ship.id} style={{ position: "relative", border: "1px solid rgba(79,195,247,0.35)", background: "rgba(79,195,247,0.06)", transition: "all 0.25s", overflow: "hidden", clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)", boxShadow: "0 0 20px rgba(79,195,247,0.08), inset 0 0 30px rgba(79,195,247,0.03)" }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(79,195,247,0.6)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(79,195,247,0.35)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                                {canManageFeatured && (
                                  <button onClick={(e) => { e.stopPropagation(); toggleFeatured(ship.id); }}
                                    style={{ position: "absolute", top: 8, left: 8, zIndex: 10, background: "rgba(5,7,15,0.85)", border: "1px solid rgba(79,195,247,0.5)", color: "#4fc3f7", fontSize: 16, cursor: "pointer", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", textShadow: "0 0 8px rgba(79,195,247,0.5)" }}>
                                    ◆
                                  </button>
                                )}
                                <Link to={`/fleet/${ship.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                                  <div style={{ width: "100%", aspectRatio: "16/9", background: "rgba(0,0,0,0.3)", overflow: "hidden", position: "relative" }}>
                                    {ship.image_url ? (
                                      <LazyImage src={ship.image_url} alt={ship.name} thumbnailWidth={480} style={{ width: "100%", height: "100%" }} />
                                    ) : (
                                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 36, color: "rgba(79,195,247,0.12)" }}>◇</div>
                                    )}
                                    <div style={{ position: "absolute", top: 6, right: 6, background: "rgba(5,7,15,0.85)", backdropFilter: "blur(8px)", padding: "4px 10px", border: "1px solid rgba(79,195,247,0.5)" }}>
                                      <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 700, color: "#4fc3f7" }}>{data.totalCount}</span>
                                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", marginLeft: 4 }}>IN USE</span>
                                    </div>
                                  </div>
                                  <div style={{ padding: "14px 16px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 15, color: "#e8eaf6", lineHeight: 1.3 }}>{ship.name}</div>
                                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 2, color: "#4fc3f7", border: "1px solid rgba(79,195,247,0.35)", padding: "1px 6px", background: "rgba(79,195,247,0.08)" }}>FEATURED</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>{ship.manufacturer || "Unknown"}</div>
                                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(79,195,247,0.7)" }}>
                                        {data.owners} {data.owners === 1 ? "owner" : "owners"}
                                      </div>
                                    </div>
                                  </div>
                                </Link>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Divider if we have both featured and non-featured */}
                    {hasFeatured && teamShips.some(s => !featuredShipIds.includes(s.id)) && (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                        <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.2)", textTransform: "uppercase" }}>Other Ships</span>
                        <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
                      </div>
                    )}

                    {/* Remaining ships — standard grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
                      {teamShips.filter(s => !featuredShipIds.includes(s.id)).map(ship => {
                        const data = shipCounts[ship.id] || { owners: 0, totalCount: 0 };
                        return (
                          <div key={ship.id} style={{ position: "relative", border: "1px solid rgba(167,139,250,0.15)", background: "rgba(255,255,255,0.02)", transition: "all 0.25s", overflow: "hidden", clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 0 100%)" }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(167,139,250,0.5)"; e.currentTarget.style.background = "rgba(167,139,250,0.06)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(167,139,250,0.15)"; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                            {canManageFeatured && (
                              <button onClick={(e) => { e.stopPropagation(); toggleFeatured(ship.id); }}
                                style={{ position: "absolute", top: 6, left: 6, zIndex: 10, background: "rgba(5,7,15,0.75)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.2)", fontSize: 14, cursor: "pointer", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", opacity: 0.6 }}
                                onMouseEnter={e => { e.currentTarget.style.color = "rgba(79,195,247,0.6)"; e.currentTarget.style.borderColor = "rgba(79,195,247,0.4)"; e.currentTarget.style.opacity = "1"; }}
                                onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.2)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.opacity = "0.6"; }}>
                                ◆
                              </button>
                            )}
                            <Link to={`/fleet/${ship.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                              <div style={{ width: "100%", aspectRatio: "16/9", background: "rgba(0,0,0,0.3)", overflow: "hidden", position: "relative" }}>
                                {ship.image_url ? (
                                  <LazyImage src={ship.image_url} alt={ship.name} thumbnailWidth={420} style={{ width: "100%", height: "100%" }} />
                                ) : (
                                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 32, color: "rgba(167,139,250,0.1)" }}>◇</div>
                                )}
                                <div style={{ position: "absolute", top: 6, right: 6, background: "rgba(5,7,15,0.85)", backdropFilter: "blur(8px)", padding: "3px 8px", border: "1px solid rgba(167,139,250,0.4)" }}>
                                  <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: "#a78bfa" }}>{data.totalCount}</span>
                                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", marginLeft: 4 }}>IN USE</span>
                                </div>
                              </div>
                              <div style={{ padding: "12px 14px" }}>
                                <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: "#e8eaf6", marginBottom: 3, lineHeight: 1.3 }}>{ship.name}</div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "rgba(232,234,246,0.35)", textTransform: "uppercase" }}>{ship.manufacturer || "Unknown"}</div>
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(167,139,250,0.7)" }}>
                                    {data.owners} {data.owners === 1 ? "owner" : "owners"}
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Reveal>
            );
          })()}

          {/* HOME BASES TAB */}
          {activeTab === "homes" && (
            <Reveal>
              <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: "rgba(232,234,246,0.4)", marginBottom: 36 }}>
                Where {team.name} members call home across the verse.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 24 }}>
                <PieChartCard
                  title="Home Bases"
                  accentColor={accent}
                  groups={homeBaseGroups}
                  expanded={expandedHomeBase}
                  onSelect={setExpandedHomeBase}
                  emptyText="No home base data"
                />
                <PieChartCard
                  title="Timezones"
                  accentColor="#4fc3f7"
                  groups={timezoneGroups}
                  expanded={expandedTimezone}
                  onSelect={setExpandedTimezone}
                  emptyText="No timezone data"
                />
              </div>
            </Reveal>
          )}

          {/* AVAILABILITY TAB */}
          {activeTab === "availability" && (
            <Reveal>
              {(() => {
                // Use the viewer's profile timezone if set; otherwise show a prompt
                const viewerTz = profile?.timezone || null;
                if (!viewerTz) {
                  return (
                    <div style={{ padding: "48px 24px", textAlign: "center" }}>
                      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 18, color: "#f59e0b", marginBottom: 12 }}>Timezone Not Set</div>
                      <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: "rgba(232,234,246,0.5)", maxWidth: 420, margin: "0 auto" }}>
                        Please set your timezone in your profile to view team availability converted to your local time.
                      </p>
                    </div>
                  );
                }
                const viewerOffset = getOffsetHours(viewerTz);
                const CHART_HEIGHT = 400;
                const memberColors = ["#60a5fa", "#34d399", "#a78bfa", "#f59e0b", "#f87171", "#38bdf8", "#4ade80", "#f472b6"];

                // Normalize availability: supports both old {start,end} and new [{start,end},...] formats
                function normalizeWindows(val) {
                  if (!val) return [];
                  if (Array.isArray(val)) return val.filter(w => w && typeof w.start === 'number' && typeof w.end === 'number');
                  if (typeof val === 'object' && val.all_day) return [{ start: 0, end: 24 }];
                  if (typeof val === 'object' && typeof val.start === 'number' && typeof val.end === 'number') return [val];
                  return [];
                }

                const memberWindows = availableMembers.map((member, idx) => {
                  const shift = viewerOffset - getOffsetHours(member.timezone || "UTC");
                  return {
                    ...member,
                    color: memberColors[idx % memberColors.length],
                    days: DAYS.map(day => {
                      const windows = normalizeWindows(member.availability?.[day]);
                      if (windows.length === 0) return [];
                      return windows.map(win => {
                        if (win.start === 0 && win.end === 24) return { start: 0, end: 24 };
                        let start = ((win.start + shift) % 24 + 24) % 24;
                        let end = ((win.end + shift) % 24 + 24) % 24;
                        if (end <= start) end += 24;
                        return { start: Math.min(start, 24), end: Math.min(end, 24) };
                      });
                    }),
                  };
                });

                const hourLabels = [0,2,4,6,8,10,12,14,16,18,20,22,24];

                return (
                  <>
                    <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "rgba(232,234,246,0.4)", marginBottom: 24 }}>
                      Times in your timezone: <span style={{ color: "#f59e0b" }}>{viewerTz}</span>
                    </p>
                    <div style={{ display: "flex", gap: 20, marginBottom: 24, flexWrap: "wrap" }}>
                      {memberWindows.map(m => (
                        <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 12, height: 12, borderRadius: 2, background: m.color }} />
                          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: "#e8eaf6" }}>{m.discord_username}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <div style={{ minWidth: 560, display: "flex" }}>
                        <div style={{ width: 52, flexShrink: 0, height: CHART_HEIGHT, position: "relative" }}>
                          {hourLabels.map(h => (
                            <div key={h} style={{ position: "absolute", top: `${(h / 24) * 100}%`, right: 8, transform: "translateY(-50%)", fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)", whiteSpace: "nowrap" }}>{fmtHour(h)}</div>
                          ))}
                        </div>
                        <div style={{ flex: 1, display: "flex", gap: 6 }}>
                          {DAYS.map((day, di) => (
                            <div key={day} style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textAlign: "center", textTransform: "uppercase", marginBottom: 6 }}>{DAY_LABELS[di]}</div>
                              <div style={{ position: "relative", height: CHART_HEIGHT, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden", display: "flex", gap: 3, padding: "0 3px" }}>
                                {hourLabels.map(h => (
                                  <div key={h} style={{ position: "absolute", top: `${(h / 24) * 100}%`, left: 0, right: 0, height: 1, background: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />
                                ))}
                                {memberWindows.map(member => {
                                  const wins = member.days[di];
                                  if (!wins || wins.length === 0) return <div key={member.id} style={{ flex: 1 }} />;
                                  return (
                                    <div key={member.id} style={{ flex: 1, position: "relative", height: "100%" }}>
                                      {wins.map((win, wi) => {
                                        const topPct = (win.start / 24) * 100;
                                        const heightPct = ((win.end - win.start) / 24) * 100;
                                        const label = win.start === 0 && win.end === 24 ? "All day" : `${fmtHour(win.start)} - ${fmtHour(win.end)}`;
                                        return (
                                          <div key={wi} title={`${member.discord_username}: ${label}`} style={{ position: "absolute", top: `${topPct}%`, height: `${heightPct}%`, left: 0, right: 0, background: member.color + "88", border: `1px solid ${member.color}`, borderRadius: 3, transition: "opacity 0.2s", cursor: "default" }}
                                            onMouseEnter={e => e.currentTarget.style.opacity = "0.7"}
                                            onMouseLeave={e => e.currentTarget.style.opacity = "1"} />
                                        );
                                      })}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </Reveal>
          )}

          {/* EVENTS TAB */}
          {activeTab === "events" && (
            <div>
              {/* Calendar/List toggle + Create Event button */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {[
                    { id: "calendar", label: "Calendar" },
                    { id: "list", label: "List" },
                  ].map(v => {
                    const active = eventsViewMode === v.id;
                    return (
                      <button key={v.id} onClick={() => setEventsViewMode(v.id)} style={{
                        background: active ? `${accent}18` : "transparent",
                        border: `1px solid ${active ? accent : "rgba(255,255,255,0.15)"}`,
                        color: active ? accent : "rgba(232,234,246,0.55)",
                        fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: active ? 700 : 400,
                        letterSpacing: 2, textTransform: "uppercase",
                        padding: "8px 18px", cursor: "pointer", transition: "all 0.2s",
                      }}>{v.label}</button>
                    );
                  })}
                </div>
                {canCreateTeamEvent && (
                  <button onClick={() => setShowEventForm(true)}
                    style={{
                      fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: 2,
                      textTransform: "uppercase", cursor: "pointer", padding: "10px 20px",
                      background: `${accent}22`, border: `1px solid ${accent}`, color: accent,
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = accent; e.currentTarget.style.color = "#05070f"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `${accent}22`; e.currentTarget.style.color = accent; }}
                  >+ Create Event</button>
                )}
              </div>

              {/* Create/Edit Event Modal — uses shared EventFormModal */}
              {showEventForm && (
                <EventFormModal
                  event={editingEvent || { team: team.name, is_org_wide: false }}
                  teams={[team.name]}
                  onSave={async (formData) => { editingEvent ? await updateTeamEvent(formData) : await saveTeamEvent(formData); }}
                  onClose={() => { setShowEventForm(false); setEditingEvent(null); }}
                  canCreateOrgEvent={isAdmin || (profile?.org_role_sort && profile.org_role_sort <= 2.9) || profile?.perm_super_admin}
                  profile={profile}
                />
              )}

              {eventsViewMode === "calendar" ? (
                <>
                  <EventsCalendar
                    events={events.filter(e => !e.archived)}
                    month={eventsCalendarMonth}
                    onMonthChange={setEventsCalendarMonth}
                    selectedDay={eventsSelectedDay}
                    onSelectDay={setEventsSelectedDay}
                    teams={[team]}
                    peakHoursByDow={peakHoursByDow}
                  />
                  {eventsSelectedDay && (() => {
                    const dayEvents = events.filter(e => !e.archived && e.date === eventsSelectedDay);
                    const dayLabel = new Date(`${eventsSelectedDay}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
                    return (
                      <div style={{ marginTop: 28 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{ width: 24, height: 2, background: accent }} />
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: accent, textTransform: "uppercase" }}>{dayLabel}</span>
                          </div>
                          <button onClick={() => setEventsSelectedDay(null)} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.45)", background: "transparent", border: "1px solid rgba(255,255,255,0.12)", padding: "6px 12px", cursor: "pointer", textTransform: "uppercase" }}>Close</button>
                        </div>
                        {dayEvents.length === 0 ? (
                          <div style={{ padding: 28, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "rgba(232,234,246,0.3)", border: "1px dashed rgba(255,255,255,0.06)", textTransform: "uppercase" }}>No events on this day</div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            {dayEvents.map(ev => {
                              const badge = ev.is_org_wide
                                ? { label: "Org-wide", color: "#fcd657" }
                                : { label: team.name, color: accent };
                              return (
                                <div key={ev.id} style={{ position: "relative" }}>
                                  <div style={{
                                    position: "absolute", top: -8, left: 12, zIndex: 2,
                                    fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2,
                                    textTransform: "uppercase", padding: "2px 8px",
                                    background: "#05070f", border: `1px solid ${badge.color}`, color: badge.color,
                                  }}>{badge.label}</div>
                                  <SharedEventCard
                                    event={ev}
                                    canEdit={canEditEvent(ev)}
                                    canJoin={!!profile}
                                    onEdit={(e) => { setEditingEvent(e); setShowEventForm(true); }}
                                    onArchive={archiveTeamEvent}
                                    onRSVP={toggleTeamRSVP}
                                    isAttending={profile ? isUserAttending(ev.attendees, profile.id) : false}
                                    profile={profile}
                                    onManageAttendees={() => {}}
                                    onShareToOrg={() => {}}
                                    canShareToOrg={false}
                                    reviewData={null}
                                    hasReminder={!!userReminders[ev.id]}
                                    reminderSchedule={userReminders[ev.id] || null}
                                    onToggleReminder={toggleTeamReminder}
                                    isHighlighted={false}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {events.length === 0 && (
                    <div style={{ textAlign: "center", padding: 40, marginTop: 14, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase" }}>
                      No events scheduled for this team
                    </div>
                  )}
                </>
              ) : (
                <>
              {/* Upcoming events */}
              {upcomingTeamEvents.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 24, height: 1, background: "rgba(232,234,246,0.15)" }} /> Upcoming ({upcomingTeamEvents.length})
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {upcomingTeamEvents.map((ev, i) => (
                      <Reveal key={ev.id} delay={i * 0.07}>
                        <SharedEventCard
                          event={ev}
                          canEdit={canEditEvent(ev)}
                          canJoin={!!profile}
                          onEdit={(e) => { setEditingEvent(e); setShowEventForm(true); }}
                          onArchive={archiveTeamEvent}
                          onRSVP={toggleTeamRSVP}
                          isAttending={profile ? isUserAttending(ev.attendees, profile.id) : false}
                          profile={profile}
                          onManageAttendees={() => {}}
                          onShareToOrg={() => {}}
                          canShareToOrg={false}
                          reviewData={null}
                          hasReminder={!!userReminders[ev.id]}
                          reminderSchedule={userReminders[ev.id] || null}
                          onToggleReminder={toggleTeamReminder}
                          isHighlighted={false}
                        />
                      </Reveal>
                    ))}
                  </div>
                </div>
              )}

              {/* Past events */}
              {pastTeamEvents.length > 0 && (
                <div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "rgba(232,234,246,0.2)", textTransform: "uppercase", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 24, height: 1, background: "rgba(232,234,246,0.08)" }} /> Past ({pastTeamEvents.length})
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {pastTeamEvents.map((ev, i) => (
                      <Reveal key={ev.id} delay={i * 0.07}>
                        <SharedEventCard
                          event={ev}
                          canEdit={canEditEvent(ev)}
                          canJoin={!!profile}
                          onEdit={(e) => { setEditingEvent(e); setShowEventForm(true); }}
                          onArchive={archiveTeamEvent}
                          onRSVP={toggleTeamRSVP}
                          isAttending={profile ? isUserAttending(ev.attendees, profile.id) : false}
                          profile={profile}
                          onManageAttendees={() => {}}
                          onShareToOrg={() => {}}
                          canShareToOrg={false}
                          reviewData={null}
                          hasReminder={!!userReminders[ev.id]}
                          reminderSchedule={userReminders[ev.id] || null}
                          onToggleReminder={toggleTeamReminder}
                          isHighlighted={false}
                        />
                      </Reveal>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {events.length === 0 && (
                <div style={{ textAlign: "center", padding: 60, fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>
                  NO EVENTS SCHEDULED
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.3)", marginTop: 8, letterSpacing: 0 }}>
                    Check the <Link to="/events" style={{ color: accent, textDecoration: "underline" }}>Events page</Link> for org-wide events
                  </div>
                </div>
              )}
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section style={{ position: "relative", zIndex: 1, padding: "100px 40px", textAlign: "center", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 500, height: 500, background: `radial-gradient(circle, ${accent}08 0%, transparent 70%)`, pointerEvents: "none" }} />
        <Reveal style={{ position: "relative", zIndex: 1 }}>
          {team.patch_url && <img src={team.patch_url} alt="" style={{ width: 72, height: 72, objectFit: "contain", marginBottom: 28, opacity: 0.65 }} onError={e => { e.target.style.display = "none"; }} />}
          <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(24px, 5vw, 52px)", color: "#e8eaf6", marginBottom: 16, lineHeight: 1.05 }}>
            Ready to Join<br />
            <span style={{ background: `linear-gradient(90deg, ${accent}, #fff, ${accent})`, backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmer 4s linear infinite" }}>{team.name}?</span>
          </h2>
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, color: "rgba(232,234,246,0.5)", maxWidth: 440, margin: "0 auto 44px", lineHeight: 1.7 }}>
            {team.tagline || "Join the crew. Make your mark."}
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="https://discord.gg/aEPQ6Zfvhd" className="glow-btn">Join via Discord</a>
            <Link to="/hierarchy" style={{ display: "inline-block", padding: "12px 32px", border: "2px solid rgba(255,255,255,0.18)", color: "rgba(232,234,246,0.55)", fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", transition: "all 0.3s ease", clipPath: "polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%)" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)"; e.currentTarget.style.color = "#e8eaf6"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; e.currentTarget.style.color = "rgba(232,234,246,0.55)"; }}>
              View All Teams
            </Link>
          </div>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer style={{ position: "relative", zIndex: 1, borderTop: "1px solid rgba(255,255,255,0.07)", padding: "40px", background: "rgba(0,0,0,0.4)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/images/logo.png" alt="Starkeeper" style={{ height: 28, width: "auto" }} onError={e => { e.target.style.display = "none"; }} />
            <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: 2.5, color: "rgba(232,234,246,0.4)" }}>STARKEEPER INDUSTRIES</span>
          </Link>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {NAV_LINKS.map(l => (
              l.external
                ? <a key={l.label} href={l.href} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase", transition: "color 0.2s" }} onMouseEnter={e => e.target.style.color = accent} onMouseLeave={e => e.target.style.color = "rgba(232,234,246,0.3)"}>{l.label}</a>
                : <Link key={l.label} to={l.href} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase", transition: "color 0.2s" }} onMouseEnter={e => e.target.style.color = accent} onMouseLeave={e => e.target.style.color = "rgba(232,234,246,0.3)"}>{l.label}</Link>
            ))}
          </div>
        </div>
        <div style={{ maxWidth: 1100, margin: "20px auto 0", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, color: "rgba(232,234,246,0.18)", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 20, lineHeight: 1.8 }}>
          &copy; 2026 Starkeeper Industries. All rights reserved. &middot; Unofficial fansite. Star Citizen&reg;, Squadron 42&reg;, Roberts Space Industries&reg;, and Cloud Imperium&reg; are registered trademarks of Cloud Imperium Rights LLC.
        </div>
      </footer>

      {/* Recruitment / Apply to Join Modal — top-level so it renders regardless of active tab */}
      {showApplyModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={e => { if (e.target === e.currentTarget) setShowApplyModal(false); }}>
          <div style={{ width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", background: "#0a0610", border: `1px solid ${accent}44`, padding: "36px 32px", position: "relative", clipPath: "polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 0 100%)" }}>
            <div style={{ position: "absolute", top: 0, left: 0, width: 24, height: 24, borderTop: `2px solid ${accent}`, borderLeft: `2px solid ${accent}` }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
              <div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: `${accent}88`, textTransform: "uppercase", marginBottom: 6 }}>Recruitment</div>
                <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 18, color: "#e8eaf6" }}>Apply to {team.name}</h3>
              </div>
              <button onClick={() => setShowApplyModal(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 20, cursor: "pointer" }}>&times;</button>
            </div>

            {!profile && (
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "rgba(232,234,246,0.55)", padding: "20px 0", textAlign: "center" }}>
                You must be signed in to apply. <a href="/auth" style={{ color: accent }}>Sign in with Discord</a>
              </div>
            )}

            {profile && (
              <>
                {vacancies.length > 0 && (
                  <div style={{ marginBottom: 22 }}>
                    <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: accent, textTransform: "uppercase", display: "block", marginBottom: 10 }}>Positions of Interest</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {[...new Map(vacancies.map(v => [v.position, v])).values()].map(v => {
                        const checked = applyPositions.includes(v.position);
                        const grp = (team.sub_groups || []).find(g => g.name === v.sub_group);
                        const vc = grp?.color || accent;
                        return (
                          <label key={v.position} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid ${checked ? vc + "66" : vc + "22"}`, background: checked ? `${vc}10` : "transparent", cursor: "pointer", transition: "all 0.15s" }}>
                            <input type="checkbox" checked={checked} onChange={() => setApplyPositions(prev => checked ? prev.filter(p => p !== v.position) : [...prev, v.position])} style={{ accentColor: vc, width: 14, height: 14 }} />
                            <div style={{ flex: 1 }}>
                              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6" }}>{v.position}</span>
                              {v.sub_group && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: vc, marginLeft: 8, letterSpacing: 1 }}>{v.sub_group}</span>}
                            </div>
                          </label>
                        );
                      })}
                      <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid rgba(255,255,255,${applyPositions.length === 0 ? "0.25" : "0.08"})`, background: applyPositions.length === 0 ? "rgba(255,255,255,0.04)" : "transparent", cursor: "pointer", transition: "all 0.15s" }}>
                        <input type="checkbox" checked={applyPositions.length === 0} onChange={() => setApplyPositions([])} style={{ width: 14, height: 14 }} />
                        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "rgba(232,234,246,0.55)" }}>No specific position — just interested</span>
                      </label>
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: 24 }}>
                  <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: accent, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Message to Leadership</label>
                  <textarea
                    rows={4}
                    value={applyMessage}
                    onChange={e => setApplyMessage(e.target.value)}
                    placeholder="Introduce yourself, share your experience, or ask a question..."
                    style={{ width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${accent}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, outline: "none", resize: "vertical" }}
                  />
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    onClick={submitApplication}
                    disabled={applySubmitting}
                    style={{ padding: "10px 28px", background: applySubmitting ? `${accent}44` : "transparent", border: `2px solid ${accent}`, color: accent, fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: applySubmitting ? "default" : "pointer", transition: "all 0.2s", clipPath: "polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)" }}
                  >
                    {applySubmitting ? "Submitting..." : "Submit Application"}
                  </button>
                  <button onClick={() => setShowApplyModal(false)} style={{ padding: "10px 20px", background: "transparent", border: "2px solid rgba(255,255,255,0.2)", color: "rgba(232,234,246,0.45)", fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", clipPath: "polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)" }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
